import { cn } from "@guru/shared/utils/cn";
import { ArrowRight, Minus, TrendingDown, TrendingUp } from "lucide-react";
import type { ReactNode } from "react";

/**
 * A trend/badge chip on a tree row. `tone` drives colour (good/bad is a
 * judgement the caller makes — "overdue down" is positive), `direction`
 * drives the arrow, and `new` renders the saffron "just appeared" dot.
 */
export interface TreeChip {
  tone: "positive" | "negative" | "neutral" | "new";
  text: string;
  direction?: "up" | "down" | "flat";
  /** Optional qualifier after a dot, e.g. "good". */
  note?: string;
}

export interface TreeLeaf {
  label: string;
  value: ReactNode;
  chip?: TreeChip;
  /** Render a drill arrow on the right, mirroring the product's clickable rows. */
  drill?: boolean;
}

interface FinanceTreeProps {
  /** Root metric. Omit for a parent-less tree (e.g. Adjustments). */
  root?: {
    label: string;
    value: ReactNode;
    chip?: TreeChip;
    /** e.g. "92% collection rate". */
    subtitle?: string;
    subtitleChip?: TreeChip;
  };
  /** Header shown when there is no root (rootless tree). */
  heading?: { label: string; caption?: string };
  leaves: TreeLeaf[];
  className?: string;
}

function Chip({ chip }: { chip: TreeChip }) {
  const Icon =
    chip.direction === "up"
      ? TrendingUp
      : chip.direction === "down"
        ? TrendingDown
        : chip.direction === "flat"
          ? Minus
          : null;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-semibold leading-none",
        chip.tone === "positive" && "bg-positive/10 text-positive",
        chip.tone === "negative" && "bg-due/10 text-due",
        chip.tone === "neutral" && "bg-muted text-muted-foreground",
        chip.tone === "new" && "bg-brand/15 text-brand-deep"
      )}
    >
      {chip.tone === "new" ? (
        <span className="h-1.5 w-1.5 rounded-full bg-brand-deep" aria-hidden="true" />
      ) : Icon ? (
        <Icon className="h-3 w-3" aria-hidden="true" />
      ) : null}
      {chip.text}
      {chip.note && <span className="font-normal opacity-80">· {chip.note}</span>}
    </span>
  );
}

/**
 * Marketing illustration of the product's Finance tab "tree": a root metric
 * (Total invoiced) with connected child leaves (Paid / Pending / Overdue /
 * Partially), or a parent-less tree of standalone aggregates (Adjustments).
 * Purely presentational — no animation of its own, so it's reduced-motion safe.
 */
export function FinanceTree({ root, heading, leaves, className }: FinanceTreeProps) {
  return (
    <div className={className}>
      {root && (
        <div className="mb-2">
          <div className="flex items-start justify-between gap-2">
            <p className="text-xs text-muted-foreground">{root.label}</p>
            {root.chip && <Chip chip={root.chip} />}
          </div>
          <p className="mt-0.5 text-xl font-bold tabular-nums text-foreground">{root.value}</p>
          {root.subtitle && (
            <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-muted-foreground">
              {root.subtitle}
              {root.subtitleChip && <Chip chip={root.subtitleChip} />}
            </p>
          )}
        </div>
      )}

      {heading && (
        <div className="mb-2">
          <p className="text-sm font-semibold text-foreground">{heading.label}</p>
          {heading.caption && <p className="text-xs text-muted-foreground">{heading.caption}</p>}
        </div>
      )}

      <div className="space-y-1.5">
        {leaves.map((leaf) => (
          <div key={leaf.label} className="relative pl-5">
            {/* Connector rail: a bridging vertical segment + horizontal tick per
                leaf, so the line always ends exactly at the last leaf — no
                overshoot — regardless of row heights. */}
            <span
              aria-hidden="true"
              className="absolute left-[5px] top-[-0.375rem] h-[calc(50%+0.375rem)] w-px bg-border"
            />
            <span
              aria-hidden="true"
              className="absolute left-[5px] top-1/2 h-px w-3.5 -translate-y-1/2 bg-border"
            />
            <div className="flex items-center justify-between gap-2 rounded-lg border border-border bg-background px-3 py-2">
              <span className="text-[13px] font-medium text-foreground">{leaf.label}</span>
              <span className="flex shrink-0 items-center gap-2">
                <span className="text-[13px] font-bold tabular-nums text-foreground">
                  {leaf.value}
                </span>
                {leaf.chip && <Chip chip={leaf.chip} />}
                {leaf.drill && (
                  <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/60" aria-hidden="true" />
                )}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
